import {FC, lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronDown, faChevronRight} from '@fortawesome/pro-light-svg-icons';
import {Route, Switch, useLocation} from 'wouter';
import './CategoryNav.less';
import NestedRoutes from '../NestedRoutes';
import Loading from '../Loading';
import ErrorBoundaryO from '../../ErrorBoundary/ErrorBoundaryO';
import Icons, {IconType} from '../IconButton/Icons';
import IconButton from '../IconButton/IconButton';
import Select from '../Select/Select';
import useComponentCache from '../../hooks/useComponentCache';
import {isMobileView} from '@/lib/picos/browserFns';
import type {SelectOption} from '@/@types/select';

export type CategoryNavItem = {
    id: string;
    label: string;
    page: string;
    icon: IconType;
    color?: string;
    title?: string;
    component: React.ComponentType;
};

export type CategoryNavCategory = {
    id: string;
    name: string;
    icon: IconType;
    items: CategoryNavItem[];
};

export type CategoryNavProps = {
    basePath: string;
    cacheKey: string;
    categories: CategoryNavCategory[];
    className?: string;
    emptyMessage?: string;
    mobileBreakpoint?: number;
};

type CacheState = {
    expandedCategories: string[];
    selectedPage: string | null;
};

function getPageIdFromPath(path: string, basePath: string): string | null {
    if (path.startsWith(basePath + '/')) {
        return path.substring(basePath.length + 1) || null;
    }
    return null;
}

function getCategoryForPage(pageId: string | null, categories: CategoryNavCategory[]): string | null {
    if (!pageId) return null;
    for (const category of categories) {
        if (category.items.some(item => item.page === pageId)) {
            return category.id;
        }
    }
    return null;
}

const CategoryNav: FC<CategoryNavProps> = (props) => {
    const {basePath, cacheKey, categories, className, emptyMessage, mobileBreakpoint} = props;
    const [location, navigate] = useLocation();
    const {cacheSave, cacheValue} = useComponentCache<CacheState>(cacheKey);

    // All category IDs for default expansion
    const allCategoryIds = useMemo(() => categories.map(c => c.id), [categories]);

    const [expandedCategories, setExpandedCategories] = useState<string[]>(
        cacheValue('expandedCategories', allCategoryIds)
    );

    // Get the currently selected page from URL
    const selectedPageId = useMemo(() => getPageIdFromPath(location, basePath), [location, basePath]);

    // Find which category the selected page belongs to
    const selectedCategoryId = useMemo(
        () => getCategoryForPage(selectedPageId, categories),
        [selectedPageId, categories]
    );

    // Restore cached page when landing on the bare base path
    const cachedPage = cacheValue('selectedPage', null);
    useEffect(() => {
        if (!selectedPageId && cachedPage) {
            // Verify the cached page still exists in the current categories
            const allPages = categories.flatMap(c => c.items).map(i => i.page);
            if (allPages.includes(cachedPage)) {
                navigate(`${basePath}/${cachedPage}`, {replace: true});
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount only

    // Save state on unmount
    useLayoutEffect(() => {
        return () => cacheSave({expandedCategories, selectedPage: selectedPageId});
    }, [cacheSave, expandedCategories, selectedPageId]);

    // Toggle category expansion
    const toggleCategory = useCallback((categoryId: string) => {
        setExpandedCategories(prev => {
            if (prev.includes(categoryId)) {
                return prev.filter(id => id !== categoryId);
            }
            return [...prev, categoryId];
        });
    }, []);

    // Navigate to an item (and expand its category)
    const selectItem = useCallback((item: CategoryNavItem) => {
        const categoryId = getCategoryForPage(item.page, categories);
        if (categoryId && !expandedCategories.includes(categoryId)) {
            setExpandedCategories(prev => [...prev, categoryId]);
        }
        navigate(`${basePath}/${item.page}`);
    }, [categories, expandedCategories, navigate, basePath]);

    const isMobile = mobileBreakpoint ? isMobileView(mobileBreakpoint) : false;

    // Mobile select dropdown
    const renderMobileSelect = () => {
        const options: SelectOption<string, string>[] = [];
        for (const category of categories) {
            for (const item of category.items) {
                const page = `${basePath}/${item.page}`;
                options.push({
                    label: <IconButton
                        id={`nav-${item.page}`}
                        type={item.icon}
                        label={item.label}
                        color={item.color}
                        onClick={() => navigate(page)}
                        clickPassthrough={false}
                    />,
                    value: page
                });
            }
        }
        const defaultValue = options.find(opt => opt.value === window.location.pathname);
        return <Select name={`${cacheKey}-pages`} options={options} defaultValue={defaultValue} />;
    };

    // Desktop sidebar
    const renderSidebar = () => (
        <div className="category-nav__list">
            {categories.map(category => {
                if (category.items.length === 0) return null;

                const isExpanded = expandedCategories.includes(category.id);
                const hasSelectedItem = category.items.some(item => item.page === selectedPageId);

                return (
                    <div key={category.id} className="category-nav__category">
                        <button
                            className={`category-nav__category-header ${hasSelectedItem ? 'category-nav__category-header--active' : ''}`}
                            onClick={() => toggleCategory(category.id)}
                            title={category.name}
                        >
                            <span className="category-nav__category-chevron">
                                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                            </span>
                            <span className="category-nav__category-icon">
                                <FontAwesomeIcon icon={category.icon.reactIcon} />
                            </span>
                            <span className="category-nav__category-name">{category.name}</span>
                            <span className="category-nav__category-count">{category.items.length}</span>
                        </button>
                        {isExpanded && (
                            <div className="category-nav__category-items">
                                {category.items.map(item => (
                                    <button
                                        key={item.id}
                                        className={`category-nav__item ${selectedPageId === item.page ? 'category-nav__item--selected' : ''}`}
                                        onClick={() => selectItem(item)}
                                        title={item.title}
                                    >
                                        <span className="category-nav__item-icon" style={item.color ? {color: item.color} : undefined}>
                                            <FontAwesomeIcon icon={item.icon.reactIcon} />
                                        </span>
                                        <span className="category-nav__item-name">{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    // Build routes from categories
    const renderRoutes = () => (
        <NestedRoutes base={basePath}>
            <Switch>
                {categories.flatMap(cat => cat.items).map(item => (
                    <Route key={item.id} path={`/${item.page}`}>
                        <ErrorBoundaryO uniqueKey={item.id} shouldUseErrorFrame={true}>
                            <Suspense fallback={<Loading center />}>
                                <item.component />
                            </Suspense>
                        </ErrorBoundaryO>
                    </Route>
                ))}
                {emptyMessage && (
                    <Route>
                        <div className="category-nav__empty">{emptyMessage}</div>
                    </Route>
                )}
            </Switch>
        </NestedRoutes>
    );

    const rootClass = ['category-nav', className].filter(Boolean).join(' ');

    return (
        <div className={rootClass}>
            <div className="category-nav__sidebar">
                {isMobile ? renderMobileSelect() : renderSidebar()}
            </div>
            <div className="category-nav__content">
                {renderRoutes()}
            </div>
        </div>
    );
};

export default CategoryNav;
